import React, { useRef } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { BiLoaderCircle } from 'react-icons/bi';
import { Icons } from '@/utils/Icons';
import CustomInput from '@/components/common/CustomInput';
import ChangePasswordModal from '@/components/modals/ChangePasswordModal';
import { useLoginPage } from '@/pages/Auth/hooks/useLogin';
import { buildTelegramLink, buildWhatsappLink, socialLinks } from '@/utils/constants';
import { useGetContactSupportQuery } from '@/apis/userApi';
import { useLockBodyScroll, useAppDispatch } from '@/hooks';
import { openModal } from '@/store/slices/modalSlice';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const LoginModal = ({ isOpen, onClose }: LoginModalProps) => {
  const { t } = useTranslation();
  const {
    form,
    handleLogin,
    handleDemoLogin,
    isLoading,
    showChangePasswordModal,
    setShowChangePasswordModal,
  } = useLoginPage({ onSuccess: onClose });

  useLockBodyScroll(isOpen);

  const dispatch = useAppDispatch();

  const { data: contactSupport } = useGetContactSupportQuery();

  const whatsappLink = buildWhatsappLink(contactSupport?.whatsapp);
  const telegramLink = buildTelegramLink(contactSupport?.telegram);

  const modalSocialLinks = [
    ...(whatsappLink ? [{ platform: 'whatsapp', url: whatsappLink }] : []),
    ...(telegramLink ? [{ platform: 'telegram', url: telegramLink }] : []),
    ...socialLinks,
  ];

  const { control, handleSubmit, reset } = form;

  const mouseDownTarget = useRef<EventTarget | null>(null);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleForgotPassword = () => {
    handleClose();
    dispatch(openModal({ name: 'forgotPassword' }));
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 px-2 md:px-4"
      onMouseDown={(e) => {
        mouseDownTarget.current = e.target;
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && mouseDownTarget.current === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <div
        className="relative w-full max-w-[400px] rounded-[10px] border-[3px] border-[#ff9500]  p-[14px] text-xs shadow-2xl bg-white dark:bg-[#18162A]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          type="button"
          onClick={handleClose}
          className="absolute right-3 top-3 text-black dark:text-white transition hover:text-red-500"
        >
          <Icons.Cross2 size={12} />
        </button>

        {/* Header */}
        <h2 className="mb-3 text-center text-base font-semibold dark:text-white text-black">
          {t('auth.login.title')}
        </h2>
        <div className="mb-6 border-b-2 border-black dark:border-white" />

        <form onSubmit={handleSubmit(handleLogin)} className="space-y-4">
          <CustomInput
            name="username"
            control={control}
            // label="User ID"
            type="text"
            placeholder={t('auth.login.userIdPlaceholder')}
            maxLength={50}
          />

          <CustomInput
            name="password"
            control={control}
            // label="Password"
            type="password"
            placeholder={t('auth.login.passwordPlaceholder')}
            maxLength={20}
          />

          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleForgotPassword}
              className="text-[11px] font-medium text-black dark:text-white hover:underline"
            >
              {t('auth.login.forgotPassword')}
            </button>
          </div>

          {/* Buttons */}
          <div className="grid grid-cols-2 gap-3 pt-1">
            <button
              type="submit"
              disabled={isLoading}
              className="flex h-11 items-center justify-center rounded-xl border border-orange-400 bg-[#08061A] text-lg font-semibold text-white [filter:drop-shadow(1px_2px_4px_#ffd04e)] transition hover:bg-white hover:text-black disabled:opacity-60 "
            >
              {isLoading ? (
                <BiLoaderCircle className="animate-spin" size={20} />
              ) : (
                t('auth.login.submit')
              )}
            </button>
            <button
              type="button"
              disabled={isLoading}
              onClick={handleDemoLogin}
              className="flex h-11 items-center justify-center rounded-xl border border-orange-400 bg-[#08061A] text-lg font-semibold text-white [filter:drop-shadow(1px_2px_4px_#ffd04e)] transition hover:bg-white hover:text-black disabled:opacity-60"
            >
              {t('auth.login.demoId')}
            </button>
          </div>
        </form>

        {/* Divider */}
        <div className="my-4 border-b border-dashed border-newLoginHeader" />

        {/* Social Section */}
        <div className="text-center">
          <p className="mb-4 text-[12px] text-black dark:text-white">{t('auth.connectWithUs')}</p>
          <div className="flex flex-wrap justify-center gap-1">
            {modalSocialLinks.map(({ platform, url }) => {
              if (!url) return null;
              return (
                <a
                  key={platform}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-10 w-10 items-center justify-center rounded-xl border border-black bg-[#FCFCFC] hover:bg-[#EBBE71]"
                >
                  <img src={`/images/social/${platform}.png`} alt={platform} />
                </a>
              );
            })}
          </div>
        </div>
      </div>

      <ChangePasswordModal
        open={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        setShowChangePasswordModal={setShowChangePasswordModal}
      />
    </div>,
    document.body
  );
};

export default LoginModal;
